import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, User, ArrowRight } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, SUBCATEGORIES, SUBCATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";

export function Navbar() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const doSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-50 glass border-b border-line" data-testid="navbar">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-[68px] gap-4">
                    <button
                        className="xl:hidden -ml-1 p-2 rounded-lg text-dim hover:text-foreground hover:bg-surface-raised transition-colors duration-150"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>

                    <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo">
                        <span className="grid place-items-center w-8 h-8 rounded-lg bg-accent-sheen shadow-glow">
                            <span className="w-2.5 h-2.5 rotate-45 rounded-[2px] bg-canvas" />
                        </span>
                        <span className="font-display text-lg sm:text-xl font-bold tracking-[-0.02em] text-foreground">
                            {brand.site_name}
                        </span>
                    </Link>

                    {/* Primary nav — desktop */}
                    <nav className="hidden xl:flex items-center gap-1 flex-1 justify-center">
                        {CATEGORIES.map((cat) => (
                            <div key={cat} className="relative group">
                                <Link
                                    to={`/category/${cat}`}
                                    data-testid={`nav-cat-${cat}`}
                                    className="flex items-center gap-1 px-3 py-2 rounded-lg text-[13px] font-medium text-dim hover:text-foreground hover:bg-surface-raised transition-colors duration-150"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    {cat === "personal-finance" && (
                                        <ChevronDown
                                            size={13}
                                            className="transition-transform duration-200 group-hover:rotate-180"
                                        />
                                    )}
                                </Link>
                                {cat === "personal-finance" && (
                                    <div className="absolute left-0 top-full pt-2 w-60 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-200 z-50">
                                        <div className="panel p-1.5 shadow-lift">
                                            {SUBCATEGORIES.map((sub) => (
                                                <Link
                                                    key={sub}
                                                    to={`/category/personal-finance/${sub}`}
                                                    data-testid={`nav-sub-${sub}`}
                                                    className="group/sub flex items-center justify-between px-3 py-2 rounded-md text-[13px] text-dim hover:text-foreground hover:bg-surface-raised transition-colors duration-150"
                                                >
                                                    {SUBCATEGORY_LABELS[sub]}
                                                    <ArrowRight size={13} className="text-accent opacity-0 -translate-x-1 transition-[opacity,transform] duration-150 group-hover/sub:opacity-100 group-hover/sub:translate-x-0" />
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <Link
                            to="/archive"
                            data-testid="nav-archive"
                            className="px-3 py-2 rounded-lg text-[13px] font-medium text-dim hover:text-foreground hover:bg-surface-raised transition-colors duration-150"
                        >
                            Archive
                        </Link>
                    </nav>

                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setSearchOpen((v) => !v)}
                            className="p-2 rounded-lg text-dim hover:text-accent hover:bg-surface-raised transition-colors duration-150"
                            aria-label="Search"
                            data-testid="navbar-search-toggle"
                        >
                            <Search size={18} />
                        </button>
                        {user ? (
                            <div className="hidden sm:flex items-center gap-1.5">
                                <Link
                                    to="/account"
                                    data-testid="navbar-account"
                                    className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-line text-[13px] text-dim hover:text-foreground hover:border-line-strong transition-colors duration-150"
                                >
                                    <span className="grid place-items-center w-6 h-6 rounded-full bg-surface-raised text-accent">
                                        <User size={12} />
                                    </span>
                                    {user.name?.split(" ")[0]}
                                </Link>
                                <button
                                    onClick={() => { logout(); navigate("/"); }}
                                    data-testid="navbar-logout"
                                    className="px-3 py-2 rounded-lg text-[13px] font-medium text-dim hover:text-neg transition-colors duration-150"
                                >
                                    Log out
                                </button>
                            </div>
                        ) : (
                            <div className="hidden sm:flex items-center gap-2">
                                <Link
                                    to="/login"
                                    data-testid="navbar-login"
                                    className="px-3 py-2 rounded-lg text-[13px] font-medium text-dim hover:text-foreground transition-colors duration-150"
                                >
                                    Log in
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="navbar-register"
                                    className="px-4 py-2 rounded-full bg-accent text-accent-foreground text-[13px] font-semibold hover:shadow-glow hover:brightness-110 transition-[filter,box-shadow] duration-150"
                                >
                                    Subscribe
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Search panel */}
            {searchOpen && (
                <div className="border-t border-line bg-surface" data-testid="navbar-search-panel">
                    <form onSubmit={doSearch} className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex gap-2">
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-dim pointer-events-none" />
                            <input
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles, markets, authors..."
                                data-testid="navbar-search-input"
                                className="field pl-10"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="navbar-search-submit"
                            className="px-6 rounded-[0.625rem] bg-accent text-accent-foreground text-sm font-semibold hover:brightness-110 transition-[filter] duration-150"
                        >
                            Search
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="xl:hidden border-t border-line bg-surface" data-testid="mobile-menu">
                    <ul className="px-3 py-3">
                        {CATEGORIES.map((cat) => (
                            <li key={cat}>
                                <Link
                                    to={`/category/${cat}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-dim hover:text-foreground hover:bg-surface-raised transition-colors duration-150"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    <ArrowRight size={14} className="text-line-strong" />
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to="/archive"
                                onClick={() => setMobileOpen(false)}
                                className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-dim hover:text-foreground hover:bg-surface-raised transition-colors duration-150"
                            >
                                Archive
                                <ArrowRight size={14} className="text-line-strong" />
                            </Link>
                        </li>
                        {!user && (
                            <li className="flex gap-2 px-3 pt-3 mt-2 border-t border-line">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2.5 rounded-full border border-line text-sm font-medium text-dim">
                                    Log in
                                </Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2.5 rounded-full bg-accent text-accent-foreground text-sm font-semibold">
                                    Subscribe
                                </Link>
                            </li>
                        )}
                        {user && (
                            <li className="px-3 pt-3 mt-2 border-t border-line space-y-2">
                                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="block w-full text-center py-2.5 rounded-full border border-line text-sm font-medium text-dim">
                                    My account
                                </Link>
                                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full py-2.5 rounded-full border border-line text-sm font-medium text-neg">
                                    Log out
                                </button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
